import { useMemo, useState } from 'react'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import ResultSheet from '../components/ResultSheet'
import CalcActions from '../components/CalcActions'
import InfoTip from '../components/InfoTip'
import { Field, SmartField, Segmented } from '../components/Inputs'
import { computeDrywall, DEFAULT_DRYWALL } from '../calculators/drywall'
import type { DrywallInputs, DrywallRoom } from '../calculators/drywall'
import { calcsInCategory } from '../data/calculators'
import { saveSheet, genId } from '../lib/storage'
import { ROOM_PRESETS, CUSTOM_DIMS, CUSTOM_ROOM_KEY, toUnit, matchedPreset, dropdownValue, presetLabel } from '../lib/roomPresets'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcName, categoryName } from '../i18n/calculators'

const PATH = '/calculators/drywall/'
const CATEGORY = 'paint-and-walls'

interface CalcContent {
  title: string
  desc: string
  h1: string
  intro: string
  includeCeiling: string
  cornerBeadLabel: string
  cornerBeadTip: string
  cornerBeadSuffix: string
  doorsTip: string
  windowsTip: string
  doorsSuffix: string
  windowsSuffix: string
  wasteTip: string
  primaryName: string
  primaryLabel: string
  savePrefix: string
  saveSummary: (sheets: number, buckets: number, rolls: number) => string
  areaSummary: (walls: number, ceiling: number, total: number, withWaste: number, needCeiling: boolean) => string
  assumptions: (total: number, wastePct: number, withWaste: number) => string
  aboutH2: string
  aboutP1: string
  howH2: string
  howP1: string
  formula: string
  howP2: string
  mistakesH3: string
  mistakes: { pre?: string; strong: string; rest: string }[]
  relatedH2: string
  reviewer: string
  faqs: QA[]
}

const EN: CalcContent = {
  title: 'Drywall Calculator — How Many Sheets Do I Need? | RenoSheets',
  desc: 'Free drywall calculator. Enter room dimensions to get sheet count plus joint compound, mesh tape, screws and corner bead. No signup.',
  h1: 'Drywall Calculator',
  intro: 'Enter your room dimensions for the exact sheet count — plus joint compound, mesh tape, screws and corner bead.',
  includeCeiling: 'Include ceiling drywall',
  cornerBeadLabel: 'Outside corner bead sticks',
  cornerBeadTip: '8-ft metal or vinyl sticks for outside corners. Count each outside corner edge separately. Default 0 — set to your actual count.',
  cornerBeadSuffix: 'sticks',
  doorsTip: 'Each door subtracts ~20 sq ft from wall area.',
  windowsTip: 'Each window subtracts ~15 sq ft from wall area.',
  doorsSuffix: 'doors',
  windowsSuffix: 'windows',
  wasteTip: 'Extra for cuts, mistakes and future repairs. 10% is standard. Add 5% more for rooms with lots of angles or arched openings.',
  primaryName: 'Drywall sheets',
  primaryLabel: 'of 4 x 8 ft sheets',
  savePrefix: 'Drywall',
  saveSummary: (sheets, buckets, rolls) => `${sheets} sheets · ${buckets} buckets joint compound · ${rolls} rolls tape`,
  areaSummary: (walls, ceiling, total, withWaste, needCeiling) =>
    `Areas: ${walls} sq ft walls${needCeiling ? ` + ${ceiling} sq ft ceiling` : ''} = ${total} sq ft net → ${withWaste} sq ft with waste`,
  assumptions: (total, wastePct, withWaste) => `Based on ${total} sq ft net (${wastePct}% waste → ${withWaste} sq ft). Sheets = 32 sq ft each.`,
  aboutH2: 'What actually affects how much drywall you need',
  aboutP1: 'Sheet count is the easy part — waste and finishing materials are where estimates go wrong. Ceilings use more compound than walls because seams run in both directions and gravity makes overhead taping slower and messier, so many pros budget an extra coat there. Room shape matters too: a simple rectangular room wastes far less than one with lots of corners, closets, or arched openings, since every inside and outside corner needs its own bead and extra compound. Sheet orientation also affects seam count — hanging sheets horizontally on 8 ft walls means one long seam instead of several short vertical ones, which cuts down on taping time and mud. This calculator starts from standard 4×8 sheets and lets you adjust waste for room complexity rather than assuming every wall is a perfect rectangle.',
  howH2: 'How to calculate drywall sheets',
  howP1: 'Drywall quantity is wall area (and ceiling if needed) divided by the sheet size, with a waste allowance for cuts:',
  formula: `wall area = (2 × (length + width)) × ceiling height
net area = wall area − doors×20 − windows×15  (sq ft)
sheets = ceil(net area × (1 + waste%) ÷ 32)`,
  howP2: 'Joint compound: 1 bucket per 500 sq ft covers three coats (tape, filler, finish). Mesh tape: 1 roll per 100 sq ft. Screws: ~6 per sheet, sold in boxes of 100.',
  mistakesH3: 'Common mistakes',
  mistakes: [
    { strong: 'Skipping primer', rest: ' before painting — fresh drywall and compound absorb paint unevenly without PVA primer.' },
    { strong: 'Too thick compound coats', rest: ' — thin coats dry faster and shrink less; 3 thin coats beats 1 thick coat every time.' },
    { strong: 'Wrong screw length', rest: ' — use 1-5/8 in coarse-thread for 1/2 in drywall into wood studs; 1-1/4 in for metal studs.' },
    { pre: 'Not ', strong: 'buying from the same lot', rest: ' — drywall thickness tolerances vary slightly between production runs.' },
  ],
  relatedH2: 'Related calculators',
  reviewer: 'Reviewed by the RenoSheets team for calculation accuracy. Method: perimeter × height minus openings, divided by 32 sq ft per sheet with adjustable waste. Last updated 2026-05-24. Estimates only — verify quantities before purchasing.',
  faqs: [
    { q: 'How many drywall sheets do I need for a 12x10 room?', a: 'A 12 × 10 ft room with 9 ft ceilings has about 396 sq ft of wall area. Subtract one door (~20 sq ft) and one window (~15 sq ft) to get ~361 sq ft net. At 32 sq ft per 4×8 sheet with 10% waste, you need about 13 sheets.' },
    { q: 'What size drywall sheet should I use?', a: '4×8 ft (48 × 96 in) is the standard size and what most calculators use. 4×12 sheets reduce the number of horizontal seams on taller walls but are heavier and harder to handle solo. 4×16 sheets are used by pros for cathedral ceilings.' },
    { q: 'How much joint compound do I need per sheet of drywall?', a: 'A rough rule is one 4.5-gallon bucket (about 62 lbs) of all-purpose joint compound per 500 sq ft. Expect three coats: tape coat, filler coat, and finish coat. Buy premixed for convenience or powder to mix as needed for fewer air pockets.' },
    { q: 'Do I need to deduct doors and windows from my drywall calculation?', a: 'Yes — subtract roughly 20 sq ft per standard door opening (3 × 7 ft = 21 sq ft, rounded) and 15 sq ft per window. However, most pros buy the extra sheet rather than deducting small openings, since returns and repairs eat into leftover material.' },
    { q: 'How many drywall screws do I need per sheet?', a: 'Plan on about 6 screws per sheet for walls: one screw every 12 in along edges (into the stud), and every 16 in in the field. A box of 100 coarse-thread 1-5/8 in screws handles about 16–17 sheets.' },
    { q: 'Should I use mesh tape or paper tape for drywall seams?', a: 'Self-adhesive fiberglass mesh tape is easier for DIYers because it sticks in place while you apply compound. Paper tape is stronger and is the professional choice — it resists cracking better long-term. Both work; the key is thin, consistent compound coats.' },
    { q: 'Do I need to prime drywall before painting?', a: 'Yes — always prime new drywall before painting. Fresh drywall and joint compound are very porous and absorb paint unevenly, causing blotchy results (called "flashing"). A PVA drywall primer seals the surface so your topcoat goes on smooth and even.' },
  ],
}

const FR: CalcContent = {
  title: 'Calculateur de gypse — Combien de feuilles me faut-il? | RenoSheets',
  desc: 'Calculateur de gypse gratuit. Entrez les dimensions de la pièce pour obtenir le nombre de feuilles, plus le composé à joints, le ruban, les vis et les baguettes de coin. Sans inscription.',
  h1: 'Calculateur de gypse',
  intro: 'Entrez les dimensions de votre pièce pour le nombre exact de feuilles — plus le composé à joints, le ruban, les vis et les baguettes de coin.',
  includeCeiling: 'Inclure le gypse du plafond',
  cornerBeadLabel: 'Baguettes de coin extérieur',
  cornerBeadTip: 'Baguettes en métal ou vinyle de 8 pi pour les coins extérieurs. Comptez chaque arête de coin extérieur séparément. Par défaut 0 — réglez selon votre nombre réel.',
  cornerBeadSuffix: 'baguettes',
  doorsTip: 'Chaque porte soustrait ~20 pi² de la surface murale.',
  windowsTip: 'Chaque fenêtre soustrait ~15 pi² de la surface murale.',
  doorsSuffix: 'portes',
  windowsSuffix: 'fenêtres',
  wasteTip: 'En plus pour les coupes, les erreurs et les réparations futures. 10 % est la norme. Ajoutez 5 % de plus pour les pièces avec beaucoup d’angles ou des ouvertures cintrées.',
  primaryName: 'Feuilles de gypse',
  primaryLabel: 'de feuilles de 4 x 8 pi',
  savePrefix: 'Gypse',
  saveSummary: (sheets, buckets, rolls) => `${sheets} feuilles · ${buckets} seaux de composé à joints · ${rolls} rouleaux de ruban`,
  areaSummary: (walls, ceiling, total, withWaste, needCeiling) =>
    `Surfaces : ${walls} pi² de murs${needCeiling ? ` + ${ceiling} pi² de plafond` : ''} = ${total} pi² net → ${withWaste} pi² avec perte`,
  assumptions: (total, wastePct, withWaste) => `Selon ${total} pi² net (${wastePct} % de perte → ${withWaste} pi²). Feuilles = 32 pi² chacune.`,
  aboutH2: 'Ce qui influence vraiment la quantité de gypse nécessaire',
  aboutP1: 'Le nombre de feuilles est la partie facile — c’est la perte et les matériaux de finition qui faussent les estimations. Les plafonds demandent plus de composé que les murs, car les joints courent dans les deux directions et le travail au-dessus de la tête est plus lent et plus salissant; plusieurs professionnels prévoient donc une couche de plus à cet endroit. La forme de la pièce compte aussi : une pièce rectangulaire simple gaspille beaucoup moins qu’une pièce pleine de coins, de garde-robes ou d’ouvertures arquées, puisque chaque coin intérieur et extérieur demande son propre ruban d’angle et du composé supplémentaire. L’orientation des feuilles influence aussi le nombre de joints — poser les feuilles à l’horizontale sur des murs de 8 pi donne un long joint plutôt que plusieurs joints verticaux courts, ce qui réduit le temps de pose du ruban et la quantité de composé. Ce calculateur part de feuilles standards de 4×8 pi et vous permet d’ajuster la perte selon la complexité de la pièce plutôt que de présumer que chaque mur est un rectangle parfait.',
  howH2: 'Comment calculer les feuilles de gypse',
  howP1: 'La quantité de gypse est la surface murale (et le plafond au besoin) divisée par la taille de la feuille, avec une marge de perte pour les coupes :',
  formula: `surface murale = (2 × (longueur + largeur)) × hauteur du plafond
surface nette = surface murale − portes×20 − fenêtres×15  (pi²)
feuilles = arrondi.sup(surface nette × (1 + % perte) ÷ 32)`,
  howP2: 'Composé à joints : 1 seau par 500 pi² couvre trois couches (rubanage, remplissage, finition). Ruban en treillis : 1 rouleau par 100 pi². Vis : ~6 par feuille, vendues en boîtes de 100.',
  mistakesH3: 'Erreurs fréquentes',
  mistakes: [
    { strong: 'Sauter l’apprêt', rest: ' avant de peindre — le gypse et le composé frais absorbent la peinture inégalement sans apprêt PVA.' },
    { strong: 'Couches de composé trop épaisses', rest: ' — les couches minces sèchent plus vite et rétrécissent moins; 3 couches minces valent mieux qu’une couche épaisse.' },
    { strong: 'Mauvaise longueur de vis', rest: ' — utilisez du filet grossier de 1 5/8 po pour du gypse de 1/2 po dans une ossature de bois; 1 1/4 po pour les montants métalliques.' },
    { pre: 'Ne pas ', strong: 'acheter dans le même lot', rest: ' — la tolérance d’épaisseur du gypse varie légèrement d’une production à l’autre.' },
  ],
  relatedH2: 'Calculateurs connexes',
  reviewer: 'Révisé par l’équipe RenoSheets pour l’exactitude des calculs. Méthode : périmètre × hauteur moins les ouvertures, divisé par 32 pi² par feuille avec une marge de perte ajustable. Dernière mise à jour 2026-05-24. Estimations seulement — vérifiez les quantités avant d’acheter.',
  faqs: [
    { q: 'Combien de feuilles de gypse pour une pièce de 12 x 10?', a: 'Une pièce de 12 × 10 pi avec des plafonds de 9 pi a environ 396 pi² de surface murale. Soustrayez une porte (~20 pi²) et une fenêtre (~15 pi²) pour obtenir ~361 pi² net. À 32 pi² par feuille de 4×8 avec 10 % de perte, il vous faut environ 13 feuilles.' },
    { q: 'Quelle taille de feuille de gypse utiliser?', a: 'Le 4×8 pi (48 × 96 po) est la taille standard et celle qu’utilisent la plupart des calculateurs. Les feuilles de 4×12 réduisent le nombre de joints horizontaux sur les murs hauts, mais sont plus lourdes et difficiles à manier seul. Les feuilles de 4×16 servent aux pros pour les plafonds cathédrale.' },
    { q: 'Combien de composé à joints par feuille de gypse?', a: 'Une règle approximative est un seau de 4,5 gallons (environ 62 lb) de composé tout usage par 500 pi². Prévoyez trois couches : rubanage, remplissage et finition. Achetez du prémélangé pour la commodité ou de la poudre à mélanger au besoin pour moins de bulles d’air.' },
    { q: 'Dois-je déduire les portes et fenêtres de mon calcul de gypse?', a: 'Oui — soustrayez environ 20 pi² par ouverture de porte standard (3 × 7 pi = 21 pi², arrondi) et 15 pi² par fenêtre. Cependant, la plupart des pros achètent la feuille en plus plutôt que de déduire les petites ouvertures, car les retours et réparations grugent le matériel restant.' },
    { q: 'Combien de vis à gypse par feuille?', a: 'Prévoyez environ 6 vis par feuille pour les murs : une vis tous les 12 po le long des rives (dans le montant) et tous les 16 po au centre. Une boîte de 100 vis à filet grossier de 1 5/8 po couvre environ 16 à 17 feuilles.' },
    { q: 'Ruban en treillis ou ruban en papier pour les joints de gypse?', a: 'Le ruban en treillis de fibre de verre autocollant est plus facile pour les bricoleurs, car il tient en place pendant l’application du composé. Le ruban en papier est plus solide et c’est le choix professionnel — il résiste mieux à la fissuration à long terme. Les deux fonctionnent; l’important est d’appliquer des couches de composé minces et régulières.' },
    { q: 'Dois-je apprêter le gypse avant de peindre?', a: 'Oui — apprêtez toujours le gypse neuf avant de peindre. Le gypse et le composé à joints frais sont très poreux et absorbent la peinture inégalement, ce qui donne un résultat tacheté (appelé « flashing »). Un apprêt PVA scelle la surface pour que votre couche de finition soit lisse et uniforme.' },
  ],
}

export default function DrywallCalculator() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)

  const [i, setI] = useState<DrywallInputs>(DEFAULT_DRYWALL)
  const [saved, setSaved] = useState(false)
  const r = useMemo(() => computeDrywall(i, locale), [i, locale])
  const set = (patch: Partial<DrywallInputs>) => {
    setI((p) => ({ ...p, ...patch }))
    setSaved(false)
  }
  const setRoom = (idx: number, patch: Partial<DrywallRoom>) =>
    set({ rooms: i.rooms.map((a, k) => (k === idx ? { ...a, ...patch } : a)) })
  const cvDim = (ft: number) => toUnit(ft, i.unit)
  const roomDefault = (idx: number) => (locale === 'fr' ? `Pièce ${idx + 1}` : `Room ${idx + 1}`)
  const applyPreset = (idx: number, label: string) => {
    if (label === CUSTOM_ROOM_KEY) {
      // Switching to "Custom room" — give the room a default name unless the
      // user already typed a custom one. Empty current (the "Select a room"
      // placeholder state) also gets the default so the input appears.
      const current = i.rooms[idx].name
      if (!current || matchedPreset(current)) setRoom(idx, { name: roomDefault(idx) })
      return
    }
    const p = ROOM_PRESETS.find((rp) => rp.label === label)
    if (!p) return
    setRoom(idx, { name: presetLabel(p, locale), length: cvDim(p.lFt), width: cvDim(p.wFt), ceilingHeight: cvDim(p.hFt) })
  }
  // New rooms start with an empty name so the dropdown shows the "Select a
  // room" placeholder — user picks a preset or "Custom room" themselves.
  const addRoom = () =>
    set({ rooms: [...i.rooms, { name: '', length: cvDim(CUSTOM_DIMS.lFt), width: cvDim(CUSTOM_DIMS.wFt), ceilingHeight: cvDim(CUSTOM_DIMS.hFt), doors: 1, windows: 1 }] })
  const removeRoom = (idx: number) => set({ rooms: i.rooms.filter((_, k) => k !== idx) })
  const lin = i.unit === 'imperial' ? (locale === 'fr' ? 'pi' : 'ft') : 'm'

  const onSave = async () => {
    const sqLabel = locale === 'fr' ? 'pi²' : 'sq ft'
    await saveSheet({
      id: genId(),
      type: 'drywall',
      name: `${c.savePrefix} — ${r.totalArea} ${sqLabel}`,
      createdAt: new Date().toISOString(),
      summary: c.saveSummary(r.sheets, r.compoundBuckets, r.tapeRolls),
      lines: r.lines.filter((l) => l.kind === 'consumable').map((l) => ({ label: l.label, qty: l.qty, unit: l.unit })),
    })
    setSaved(true)
  }

  const related = calcsInCategory(CATEGORY).filter((x) => x.slug !== 'drywall')

  // Step-by-step "How we got this" content for the ⓘ button on the result card.
  // Mirrors the math in computeDrywall: wall area − openings + (optional) ceiling,
  // × (1 + waste%), ÷ 32 sq ft per sheet, rounded up.
  const sqftWord = locale === 'fr' ? 'pi²' : 'sq ft'
  const buildExplain = (): React.ReactNode => {
    if (i.inputMode === 'area') {
      return (
        <ol className="list-decimal space-y-1 pl-5">
          <li>{locale === 'fr' ? 'Surface saisie' : 'Area you entered'}: <strong>{r.totalArea} {sqftWord}</strong></li>
          <li>{locale === 'fr' ? `+ ${i.wastePct} % de perte` : `+ ${i.wastePct}% waste`}: <strong>{r.areaWithWaste} {sqftWord}</strong></li>
          <li>{locale === 'fr' ? '÷ 32 pi²/feuille (4 × 8 pi)' : '÷ 32 sq ft/sheet (4 × 8 ft)'} = <strong>{(r.areaWithWaste / 32).toFixed(2)}</strong></li>
          <li>{locale === 'fr' ? 'Arrondi à la feuille entière' : 'Rounded up to whole sheets'} = <strong>{r.sheets} {locale === 'fr' ? (r.sheets === 1 ? 'feuille' : 'feuilles') : (r.sheets === 1 ? 'sheet' : 'sheets')}</strong></li>
        </ol>
      )
    }
    // Dimensions mode — break down room-by-room wall math
    const wallGross = i.rooms.reduce((s, rm) => s + 2 * (rm.length + rm.width) * rm.ceilingHeight, 0)
    const openings = i.rooms.reduce((s, rm) => s + Math.max(0, rm.doors) * 20 + Math.max(0, rm.windows) * 15, 0)
    const totalDoors = i.rooms.reduce((s, rm) => s + Math.max(0, rm.doors), 0)
    const totalWindows = i.rooms.reduce((s, rm) => s + Math.max(0, rm.windows), 0)
    return (
      <ol className="list-decimal space-y-1 pl-5">
        <li>
          {locale === 'fr' ? 'Surface brute des murs' : 'Gross wall area'} ({i.rooms.length}{' '}
          {locale === 'fr' ? (i.rooms.length === 1 ? 'pièce' : 'pièces') : (i.rooms.length === 1 ? 'room' : 'rooms')}):
          {' '}<strong>{Math.round(wallGross)} {sqftWord}</strong>
        </li>
        <li>
          {locale === 'fr'
            ? `− Ouvertures (${totalDoors} porte${totalDoors === 1 ? '' : 's'} × 20 + ${totalWindows} fenêtre${totalWindows === 1 ? '' : 's'} × 15)`
            : `− Openings (${totalDoors} door${totalDoors === 1 ? '' : 's'} × 20 + ${totalWindows} window${totalWindows === 1 ? '' : 's'} × 15)`}:
          {' '}<strong>−{openings} {sqftWord}</strong>
        </li>
        <li>= {locale === 'fr' ? 'Murs nets' : 'Net walls'}: <strong>{r.wallArea} {sqftWord}</strong></li>
        {i.needCeiling && (
          <li>{locale === 'fr' ? '+ Plafonds' : '+ Ceilings'}: <strong>{r.ceilingArea} {sqftWord}</strong></li>
        )}
        <li>= {locale === 'fr' ? 'Surface totale nette' : 'Total net area'}: <strong>{r.totalArea} {sqftWord}</strong></li>
        <li>{locale === 'fr' ? `+ ${i.wastePct} % de perte` : `+ ${i.wastePct}% waste`}: <strong>{r.areaWithWaste} {sqftWord}</strong></li>
        <li>{locale === 'fr' ? '÷ 32 pi²/feuille (4 × 8 pi)' : '÷ 32 sq ft/sheet (4 × 8 ft)'} = <strong>{(r.areaWithWaste / 32).toFixed(2)}</strong></li>
        <li>{locale === 'fr' ? 'Arrondi à la feuille entière' : 'Rounded up to whole sheets'} = <strong>{r.sheets} {locale === 'fr' ? (r.sheets === 1 ? 'feuille' : 'feuilles') : (r.sheets === 1 ? 'sheet' : 'sheets')}</strong></li>
      </ol>
    )
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <Seo title={c.title} description={c.desc} path={PATH} schema={softwareAppSchema(calcName('drywall', 'Drywall Calculator', locale), c.desc, localizedHref(PATH, locale))} />
      <Breadcrumbs
        items={[
          { name: tr('crumb.home'), href: L('/') },
          { name: categoryName(CATEGORY, 'Paint & Walls', locale), href: L(`/calculators/${CATEGORY}/`) },
          { name: calcName('drywall', 'Drywall Calculator', locale), href: L(PATH) },
        ]}
      />

      <h1 className="text-3xl">{c.h1}</h1>
      <p className="mt-1 max-w-content text-text-muted">{c.intro}</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>
          <div className="mb-5 flex flex-wrap gap-6">
            <Segmented
              label={tr('calc.input')}
              value={i.inputMode}
              onChange={(v) => {
                if (v === 'area' && i.inputMode === 'dimensions') {
                  // Use the computed totalArea (in sq ft) and convert to user's unit.
                  const inUserUnit = i.unit === 'metric' ? r.totalArea * 0.09290304 : r.totalArea
                  set({ inputMode: v, totalArea: Math.round(inUserUnit * 10) / 10 })
                } else {
                  set({ inputMode: v })
                }
              }}
              options={[{ v: 'dimensions', l: tr('calc.dimensions') }, { v: 'area', l: tr('calc.totalArea') }]}
            />
          </div>

          {i.inputMode === 'area' ? (
            <div className="rounded-md border border-border p-3">
              <SmartField
                label={tr('calc.totalArea')}
                imperialValue={i.totalArea}
                onChangeImperial={(n) => set({ totalArea: n })}
                imperialUnit="sq ft"
              />
              <p className="mt-2 text-xs text-text-subtle">{tr('calc.areaHint')}</p>
            </div>
          ) : (
          /* Rooms */
          <div className="space-y-3">
            {i.rooms.map((room, idx) => {
              const isCustom = dropdownValue(room.name) === CUSTOM_ROOM_KEY
              return (
              <div key={idx} className="rounded-md border border-border p-3">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <select
                    value={dropdownValue(room.name)}
                    onChange={(e) => applyPreset(idx, e.target.value)}
                    aria-label={tr('calc.roomType')}
                    className={`rounded-md border border-border bg-surface-2 px-2 py-1.5 text-sm text-text-body focus:border-brand focus:outline-none ${isCustom ? '' : 'w-full sm:w-1/2'}`}
                  >
                    <option value="" disabled>{tr('calc.selectRoom')}</option>
                    {ROOM_PRESETS.map((p) => (
                      <option key={p.label} value={p.label}>{presetLabel(p, locale)}</option>
                    ))}
                    <option value={CUSTOM_ROOM_KEY}>{tr('calc.customRoom')}</option>
                  </select>
                  {isCustom && (
                    <input
                      type="text"
                      value={room.name ?? ''}
                      onChange={(e) => setRoom(idx, { name: e.target.value })}
                      placeholder={tr('calc.roomName')}
                      className="min-w-0 flex-1 rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text outline-none focus:border-brand"
                    />
                  )}
                  {i.rooms.length > 1 && (
                    <button type="button" onClick={() => removeRoom(idx)} className="text-xs text-danger hover:underline whitespace-nowrap">{tr('calc.remove')}</button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <SmartField label={tr('calc.length')} imperialValue={room.length} onChangeImperial={(n) => setRoom(idx, { length: n })} imperialUnit="ft" />
                  <SmartField label={tr('calc.width')} imperialValue={room.width} onChangeImperial={(n) => setRoom(idx, { width: n })} imperialUnit="ft" />
                  <SmartField label={tr('calc.ceilingHeight')} imperialValue={room.ceilingHeight} onChangeImperial={(n) => setRoom(idx, { ceilingHeight: n })} imperialUnit="ft" />
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>
                    <span className="mb-1 flex items-center text-xs font-medium text-text-body">
                      {tr('calc.doors')}
                      <InfoTip text={c.doorsTip} />
                    </span>
                    <Field label="" value={room.doors} onChange={(n) => setRoom(idx, { doors: Math.round(n) })} suffix={c.doorsSuffix} />
                  </div>
                  <div>
                    <span className="mb-1 flex items-center text-xs font-medium text-text-body">
                      {tr('calc.windows')}
                      <InfoTip text={c.windowsTip} />
                    </span>
                    <Field label="" value={room.windows} onChange={(n) => setRoom(idx, { windows: Math.round(n) })} suffix={c.windowsSuffix} />
                  </div>
                </div>
              </div>
              )
            })}
            <button type="button" onClick={addRoom} className="w-full rounded-md border border-dashed border-border py-2 text-sm font-medium text-brand-text hover:bg-surface-2">
              {tr('calc.addRoom')}
            </button>
          </div>
          )}

          {/* Options */}
          <div className="mt-4 space-y-3">
            {/* needCeiling only applies in dimensions mode — in area mode the user
                has already chosen what their total covers. */}
            {i.inputMode === 'dimensions' && (
              <label className="flex items-center gap-2 text-sm text-text-body">
                <input type="checkbox" checked={i.needCeiling} onChange={(e) => set({ needCeiling: e.target.checked })} />
                {c.includeCeiling}
              </label>
            )}

            <div>
              <span className="mb-1 flex items-center text-sm font-medium text-text-body">
                {c.cornerBeadLabel}
                <InfoTip text={c.cornerBeadTip} />
              </span>
              <Field label="" value={i.cornerBeadSticks} onChange={(n) => set({ cornerBeadSticks: Math.round(n) })} suffix={c.cornerBeadSuffix} />
            </div>
          </div>

          {/* Waste */}
          <div className="mt-5 rounded-lg bg-surface-2 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center font-medium text-text-body">
                {tr('calc.wasteAllowance')}
                <InfoTip text={c.wasteTip} />
              </span>
              <span className="tnum text-brand-text">{i.wastePct}%</span>
            </div>
            <input
              type="range" min={5} max={20} step={1} value={i.wastePct}
              onChange={(e) => set({ wastePct: parseInt(e.target.value) })}
              className="mt-1 w-full accent-brand"
            />
            <p className="mt-2 text-xs text-text-subtle">
              {c.areaSummary(r.wallArea, r.ceilingArea, r.totalArea, r.areaWithWaste, i.needCeiling)}
            </p>
          </div>
          <CalcActions onSave={onSave} saved={saved} locale={locale} className="mt-6 border-t border-border pt-4" />
        </form>

        <div className="lg:col-span-2">
          <ResultSheet
            primary={{ name: c.primaryName, label: c.primaryLabel, qty: r.sheets, unit: 'sheet', query: '4x8 drywall sheet 1/2 inch' }}
            secondary={null}
            lines={r.lines}
            assumptions={c.assumptions(r.totalArea, i.wastePct, r.areaWithWaste)}
            explain={buildExplain()}
          />
        </div>
      </div>

      <article className="mx-auto mt-8 max-w-content text-text-body">
        <h2 className="text-2xl">{c.aboutH2}</h2>
        <p className="mt-3">{c.aboutP1}</p>
        <h2 className="mt-8 text-2xl">{c.howH2}</h2>
        <p className="mt-3">{c.howP1}</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-surface-2 p-3 text-sm text-text">{c.formula}</pre>
        <p className="mt-3">{c.howP2}</p>
        <h3 className="mt-6 text-xl">{c.mistakesH3}</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {c.mistakes.map((m, k) => <li key={k}>{m.pre}<strong>{m.strong}</strong>{m.rest}</li>)}
        </ul>
      </article>

      <Faq items={c.faqs} />

      <section className="mt-10">
        <h2 className="text-2xl">{c.relatedH2}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {related.map((x) => (
            <a
              key={x.slug}
              href={L(`/calculators/${x.slug}/`)}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                x.live
                  ? 'border-border bg-surface-2 font-medium text-brand-text hover:border-brand hover:bg-surface'
                  : 'border-dashed border-border bg-surface-2 text-text-muted'
              }`}
            >
              {calcName(x.slug, x.name, locale)} {x.live ? '' : <span className="text-text-subtle">{tr('calc.soon')}</span>}
            </a>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-8 max-w-content border-t border-border pt-4 text-xs text-text-subtle">{c.reviewer}</div>
    </div>
  )
}
